import { Dashboard } from '../../models/Dashboard'
import { Payee } from '../../models/Payee'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    employees: Payee[]
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const getRoleTitle = (user?: User) => {
    switch (user?.role) {
        case 'admin':
            return 'Administrator'
        case 'employee':
            return 'Staff'
        case 'contractor':
            return 'Limited'
        case 'owner':
            return user.entity ?? 'Owner'
        default:
            return user?.role ?? 'Unknown'
    }
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.employees.map((e) => RecordFactory.getInstance().loadPayee(e.id!))
    )
}

const render = ({
    base: {
        props: { employees, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: employees.length === 1 ? `Team member` : `Team members`,
                        value: (dashboard?.value ?? 0).toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Employee</TableHead>
                        <TableHead className="font-normal">Role</TableHead>
                        <TableHead className="font-normal">Joined</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {employees.map((e) => (
                        <ClickableTableRow key={e.id} href={`/team-members/${e.person?.id}`}>
                            <TableCell>
                                <ClientTableCell user={e.person} />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm" className="capitalize">
                                    {getRoleTitle(e.person)}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">
                                    {e.person?.createdDate?.format('MMM D, YYYY')}
                                </Paragraph>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {employees.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const EmployeesDashboard = usePSComponent(render, viewWillAppear)
